import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Primary CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactLinkDef: SlotGroupDefinition = {
  label: 'Contact Us Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/**
 * Preferred Employer Program — verify-your-eligibility CTA.
 *
 * Gradient secondary-color callout with two side-by-side CTAs: a primary
 * tour button and a secondary "Contact Us" link, both driven by slot
 * groups so each site can point them anywhere without a code change.
 */
export function PreferredEmployerCTA() {
  const ctaLink = useSlotGroup('preferredemployercta.cta_link', ctaLinkDef);
  const contactLink = useSlotGroup('preferredemployercta.contact_link', contactLinkDef);

  useRevealStyles();
  const calloutRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pep-cta">
      <div className="pep-cta-container">
        {/* Primary callout */}
        <div
          ref={calloutRef}
          className="reveal-fade-up pep-callout glow-trace-wrap relative"
        >
          <GlowTrace delay={0.3} />
          <RichTextContent
            path="preferredemployercta.callout_title"
            as="h2"
            className="pep-callout-title"
          />
          <RichTextContent
            path="preferredemployercta.callout_body"
            as="p"
            className="pep-callout-body"
          />

          <div className="pep-callout-actions">
            <EditableSlotGroup
              groupPath="preferredemployercta.cta_link"
              definition={ctaLinkDef}
            >
              <a
                href={ctaLink.url || '/schedule-a-tour'}
                target={ctaLink._target || '_self'}
                className="pep-cta-btn"
              >
                {ctaLink.label || 'Schedule A Tour'}
              </a>
            </EditableSlotGroup>

            <EditableSlotGroup
              groupPath="preferredemployercta.contact_link"
              definition={contactLinkDef}
            >
              <a
                href={contactLink.url || '/contact-us'}
                target={contactLink._target || '_self'}
                className="pep-cta-btn"
              >
                {contactLink.label || 'Contact Us'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .pep-cta {
          padding: 3rem 0 3.5rem;
          background: var(--vibe-color-background, #ffffff);
        }
        @media (min-width: 768px) {
          .pep-cta { padding: 4rem 0 5rem; }
        }

        .pep-cta-container {
          max-width: 64rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) {
          .pep-cta-container { padding: 0 1.5rem; }
        }

        /* Callout */
        .pep-callout {
          background: linear-gradient(
            135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 82%, black) 100%
          );
          color: var(--vibe-color-text-on-secondary, #fff);
          text-align: center;
          padding: 2.5rem 1.5rem;
          border-radius: var(--vibe-radius-lg, 8px);
        }
        @media (min-width: 768px) {
          .pep-callout { padding: 3.25rem 2.5rem; }
        }

        .pep-callout-title {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.5vw, 2.15rem);
          font-weight: 400;
          margin: 0 0 0.85rem;
          letter-spacing: 0.01em;
          color: inherit;
        }
        .pep-callout-title p { margin: 0; }

        .pep-callout-body {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          max-width: 40rem;
          margin: 0 auto 1.75rem;
          opacity: 0.95;
        }
        .pep-callout-body p { margin: 0; }

        .pep-callout-actions {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.85rem;
        }
        @media (min-width: 520px) {
          .pep-callout-actions { flex-direction: row; }
        }

        .pep-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: var(--vibe-font-body);
          justify-content: center;
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          line-height: 1.3;
          padding: 0.85rem 1.75rem;
          text-decoration: none;
          color: var(--vibe-color-text);
          background: white;
          border-radius: 9999px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .pep-cta-btn:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 75%, black);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerCTA',
  displayName: 'Preferred Employer — How It Works & CTA',
  tokens: {
    'preferredemployercta.callout_title': {
      label: 'Callout Title',
      type: 'richtext',
      defaultValue: '<p>Ready to Claim Your Benefits?</p>',
    },
    'preferredemployercta.callout_body': {
      label: 'Callout Body',
      type: 'richtext',
      defaultValue:
        '<p>Schedule a tour to experience <span data-type="field-token" data-field="property_name"></span> in person, or contact our leasing team to confirm your eligibility and unlock your Preferred Employer savings.</p>',
    },
  },
  slotGroups: {
    'preferredemployercta.cta_link': ctaLinkDef,
    'preferredemployercta.contact_link': contactLinkDef,
  },
};

(PreferredEmployerCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerCTA;
